import type { ReactNode } from 'react'

/**
 * A small Markdown renderer for the docs this app ships (currently
 * `docs/voltage.md`).
 *
 * It builds React nodes rather than HTML, so nothing reaches
 * `dangerouslySetInnerHTML` and a document can never inject markup. The subset
 * is the one a technical write-up actually uses: headings, paragraphs, lists,
 * tables, block quotes, fenced code, rules, and inline code/emphasis/links.
 * Anything outside the subset degrades to plain text rather than disappearing.
 *
 * Pulling in a Markdown library would mean a new dependency and a lockfile
 * change on a branch that has seven siblings open; this is the cheaper trade.
 */

const EXTERNAL_LINK = /^(https?:)?\/\//
const INLINE = /`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|\[[^\]]+\]\([^)\s]+\)/g

/** Split one run of text into React nodes, honouring inline Markdown. */
const renderInline = (text: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = []
  let cursor = 0
  let sequence = 0

  const push = (node: ReactNode): void => {
    nodes.push(node)
    sequence += 1
  }

  ;[...text.matchAll(INLINE)].forEach((match) => {
    const token = match[0]
    const offset = match.index
    if (offset > cursor) push(text.slice(cursor, offset))
    cursor = offset + token.length

    const key = `${keyPrefix}-i${sequence}`

    if (token.startsWith('`'))
      push(
        <code key={key} className="ev-md__code">
          {token.slice(1, -1)}
        </code>,
      )
    else if (token.startsWith('**')) push(<strong key={key}>{token.slice(2, -2)}</strong>)
    else if (token.startsWith('*') || token.startsWith('_'))
      push(<em key={key}>{token.slice(1, -1)}</em>)
    else {
      const split = token.indexOf('](')
      const href = token.slice(split + 2, -1)
      const external = EXTERNAL_LINK.test(href)
      push(
        <a
          key={key}
          href={href}
          {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
        >
          {token.slice(1, split)}
        </a>,
      )
    }
  })

  if (cursor < text.length) push(text.slice(cursor))
  return nodes
}

interface Keyed {
  readonly id: string
  readonly text: string
}

type Block =
  | { readonly kind: 'heading'; readonly id: string; readonly level: number; readonly text: string }
  | { readonly kind: 'paragraph'; readonly id: string; readonly text: string }
  | { readonly kind: 'quote'; readonly id: string; readonly text: string }
  | { readonly kind: 'code'; readonly id: string; readonly text: string }
  | { readonly kind: 'rule'; readonly id: string }
  | {
      readonly kind: 'list'
      readonly id: string
      readonly ordered: boolean
      readonly items: readonly Keyed[]
    }
  | {
      readonly kind: 'table'
      readonly id: string
      readonly head: readonly Keyed[]
      readonly rows: readonly { readonly id: string; readonly cells: readonly Keyed[] }[]
    }

const HEADING = /^(#{1,6})\s+(.*)$/
const BULLET = /^\s*[-*+]\s+(.*)$/
const ORDERED = /^\s*\d+[.)]\s+(.*)$/
const RULE = /^\s*([-*_])\1{2,}\s*$/
const QUOTE = /^\s*>\s?(.*)$/
const TABLE_ROW = /^\s*\|(.+)\|\s*$/
const TABLE_DIVIDER = /^\s*\|?[\s:|-]+\|[\s:|-]*$/

const splitRow = (line: string, keyPrefix: string): Keyed[] =>
  line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((cell, column) => ({ id: `${keyPrefix}c${column}`, text: cell.trim() }))

/** Group lines into blocks. One pass, no lookbehind, no recursion. */
const parseBlocks = (source: string): Block[] => {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  let index = 0
  let sequence = 0

  const nextId = (): string => {
    sequence += 1
    return `b${sequence}`
  }

  while (index < lines.length) {
    const line = lines[index] ?? ''

    if (line.trim() === '') {
      index += 1
      continue
    }

    if (line.startsWith('```')) {
      const body: string[] = []
      index += 1
      while (index < lines.length && !(lines[index] ?? '').startsWith('```')) {
        body.push(lines[index] ?? '')
        index += 1
      }
      index += 1
      blocks.push({ kind: 'code', id: nextId(), text: body.join('\n') })
      continue
    }

    if (RULE.test(line)) {
      blocks.push({ kind: 'rule', id: nextId() })
      index += 1
      continue
    }

    const heading = HEADING.exec(line)
    if (heading !== null) {
      blocks.push({
        kind: 'heading',
        id: nextId(),
        level: (heading[1] ?? '#').length,
        text: heading[2] ?? '',
      })
      index += 1
      continue
    }

    if (TABLE_ROW.test(line) && TABLE_DIVIDER.test(lines[index + 1] ?? '')) {
      const id = nextId()
      const head = splitRow(line, `${id}h`)
      const rows: { id: string; cells: Keyed[] }[] = []
      index += 2
      while (index < lines.length && TABLE_ROW.test(lines[index] ?? '')) {
        const rowId = `${id}r${rows.length}`
        rows.push({ id: rowId, cells: splitRow(lines[index] ?? '', rowId) })
        index += 1
      }
      blocks.push({ kind: 'table', id, head, rows })
      continue
    }

    const quote = QUOTE.exec(line)
    if (quote !== null) {
      const body: string[] = [quote[1] ?? '']
      index += 1
      while (index < lines.length) {
        const next = QUOTE.exec(lines[index] ?? '')
        if (next === null) break
        body.push(next[1] ?? '')
        index += 1
      }
      blocks.push({ kind: 'quote', id: nextId(), text: body.join(' ').trim() })
      continue
    }

    const ordered = ORDERED.test(line)
    const listMatch = ordered ? ORDERED.exec(line) : BULLET.exec(line)
    if (listMatch !== null) {
      const id = nextId()
      const texts: string[] = [listMatch[1] ?? '']
      index += 1
      while (index < lines.length) {
        const candidate = lines[index] ?? ''
        const next = (ordered ? ORDERED : BULLET).exec(candidate)
        if (next !== null) {
          texts.push(next[1] ?? '')
          index += 1
          continue
        }
        // A wrapped item: the author hard-wrapped one bullet across two lines,
        // so fold it back rather than spilling the tail out as a paragraph.
        if (candidate.trim() === '' || !candidate.startsWith(' ')) break
        texts[texts.length - 1] = `${texts[texts.length - 1] ?? ''} ${candidate.trim()}`
        index += 1
      }
      blocks.push({
        kind: 'list',
        id,
        ordered,
        items: texts.map((text, item) => ({ id: `${id}i${item}`, text })),
      })
      continue
    }

    const paragraph: string[] = [line.trim()]
    index += 1
    while (index < lines.length) {
      const next = lines[index] ?? ''
      if (
        next.trim() === '' ||
        next.startsWith('```') ||
        HEADING.test(next) ||
        RULE.test(next) ||
        QUOTE.test(next) ||
        BULLET.test(next) ||
        ORDERED.test(next) ||
        TABLE_ROW.test(next)
      )
        break
      paragraph.push(next.trim())
      index += 1
    }
    blocks.push({ kind: 'paragraph', id: nextId(), text: paragraph.join(' ') })
  }

  return blocks
}

/**
 * Documents start at `#`, and both the modal and the about page already own an
 * `<h1>`, so every level shifts down one to keep the outline valid.
 */
const Heading = ({ level, id, text }: { level: number; id: string; text: string }) => {
  const children = renderInline(text, id)
  if (level <= 1) return <h2 className="ev-md__h2">{children}</h2>
  if (level === 2) return <h3 className="ev-md__h3">{children}</h3>
  if (level === 3) return <h4 className="ev-md__h4">{children}</h4>
  return <h5 className="ev-md__h5">{children}</h5>
}

const Table = ({ block }: { block: Extract<Block, { kind: 'table' }> }) => (
  <div className="ev-md__table-wrap">
    <table className="ev-md__table">
      <thead>
        <tr>
          {block.head.map((cell) => (
            <th key={cell.id} scope="col">
              {renderInline(cell.text, cell.id)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.rows.map((row) => (
          <tr key={row.id}>
            {row.cells.map((cell) => (
              <td key={cell.id}>{renderInline(cell.text, cell.id)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const renderBlock = (block: Block): ReactNode => {
  switch (block.kind) {
    case 'heading':
      return <Heading key={block.id} level={block.level} id={block.id} text={block.text} />
    case 'code':
      return (
        <pre key={block.id} className="ev-md__pre">
          <code>{block.text}</code>
        </pre>
      )
    case 'rule':
      return <hr key={block.id} className="ev-md__rule" />
    case 'quote':
      return (
        <blockquote key={block.id} className="ev-md__quote">
          {renderInline(block.text, block.id)}
        </blockquote>
      )
    case 'table':
      return <Table key={block.id} block={block} />
    case 'list': {
      const items = block.items.map((item) => (
        <li key={item.id}>{renderInline(item.text, item.id)}</li>
      ))
      return block.ordered ? (
        <ol key={block.id} className="ev-md__list">
          {items}
        </ol>
      ) : (
        <ul key={block.id} className="ev-md__list">
          {items}
        </ul>
      )
    }
    default:
      return (
        <p key={block.id} className="ev-md__p">
          {renderInline(block.text, block.id)}
        </p>
      )
  }
}

export const Markdown = ({ source }: { source: string }) => (
  <div className="ev-md">{parseBlocks(source).map(renderBlock)}</div>
)
